import React, { useState, useMemo, useEffect } from 'react';
import { TrendingDown, Calculator, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { trackLanding } from '../../lib/landingTrack';

/**
 * Calculateur ROI — interactif live.
 *
 * 4 inputs : poste · niveau (junior/senior) · durée (12/24/36 mois) · nb talents (1-5)
 * Outputs  : coût Suisse · coût Talent Partner · économie mensuelle / annuelle / totale
 *
 * Tarifs CHF/mois (charges incluses, base employeur Suisse — estimation 2026).
 */

const POSTES = [
  { id: 'secretaire', label: 'Secrétaire / Assistante administrative', ch: { junior: 5800, senior: 6800 }, tp: { junior: 650, senior: 950 } },
  { id: 'assistante', label: 'Assistante virtuelle', ch: { junior: 5500, senior: 6500 }, tp: { junior: 600, senior: 900 } },
  { id: 'service_client', label: 'Service client / Prise d’appels', ch: { junior: 5500, senior: 6500 }, tp: { junior: 700, senior: 1000 } },
  { id: 'community', label: 'Community Manager', ch: { junior: 5800, senior: 7000 }, tp: { junior: 850, senior: 1300 } },
  { id: 'media_buyer', label: 'Media Buyer (Meta / Google Ads)', ch: { junior: 6800, senior: 8500 }, tp: { junior: 1100, senior: 1900 } },
  { id: 'developpeur', label: 'Développeur / Automation', ch: { junior: 8500, senior: 11500 }, tp: { junior: 1800, senior: 2800 } },
];

const NIVEAUX = [
  { id: 'junior', label: 'Junior' },
  { id: 'senior', label: 'Senior' },
];

const DUREES = [12, 24, 36];

const fmtCHF = (n) =>
  new Intl.NumberFormat('fr-CH', {
    style: 'currency',
    currency: 'CHF',
    maximumFractionDigits: 0,
  }).format(n);

const RoiCalculator = ({ onCtaClick }) => {
  const [posteId, setPosteId] = useState('secretaire');
  const [niveau, setNiveau] = useState('junior');
  const [duree, setDuree] = useState(12);
  const [nbTalents, setNbTalents] = useState(1);

  const poste = useMemo(
    () => POSTES.find((p) => p.id === posteId) || POSTES[0],
    [posteId]
  );

  const calc = useMemo(() => {
    const chMonthly = poste.ch[niveau] * nbTalents;
    const tpMonthly = poste.tp[niveau] * nbTalents;
    const monthlySaving = chMonthly - tpMonthly;
    const annualSaving = monthlySaving * 12;
    const totalSaving = monthlySaving * duree;
    return { chMonthly, tpMonthly, monthlySaving, annualSaving, totalSaving };
  }, [poste, niveau, duree, nbTalents]);

  useEffect(() => {
    trackLanding('roi_generated', {
      meta: { poste: posteId, niveau, duree, nb: nbTalents },
    });
  }, [posteId, niveau, duree, nbTalents]);

  const handlePosteChange = (e) => {
    const v = e.target.value;
    setPosteId(v);
    trackLanding('poste_select', { meta: { poste: v }, dedupe: false });
  };

  return (
    <section
      id="calculateur"
      className="py-16 sm:py-24 px-4 bg-tp-bg"
      data-testid="roi-calculator"
    >
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10 sm:mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-tp-primary/10 text-tp-primary text-xs font-semibold mb-5 uppercase tracking-wide">
            <Calculator className="h-3.5 w-3.5" />
            Calculateur d’économie
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-tp-primary leading-tight">
            Combien coûte réellement ce poste&nbsp;?
          </h2>
          <p className="text-tp-text-light mt-4 max-w-2xl mx-auto text-base sm:text-lg">
            Ajustez les paramètres ci-dessous. L’économie se met à jour en direct.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 lg:gap-10 items-start">
          {/* INPUTS */}
          <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 border border-tp-border">
            <h3 className="font-display text-xl font-semibold text-tp-primary mb-6">
              Vos paramètres
            </h3>

            <div className="space-y-5">
              <div>
                <label
                  htmlFor="roi-poste"
                  className="text-sm font-medium text-tp-text mb-1.5 block"
                >
                  Poste recherché
                </label>
                <select
                  id="roi-poste"
                  value={posteId}
                  onChange={handlePosteChange}
                  className="w-full max-w-full px-4 py-3 rounded-lg border border-tp-border bg-white focus:outline-none focus:ring-2 focus:ring-tp-accent text-sm sm:text-base truncate"
                  data-testid="roi-poste"
                >
                  {POSTES.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <span className="text-sm font-medium text-tp-text mb-1.5 block">Niveau</span>
                <div className="grid grid-cols-2 gap-2" role="radiogroup">
                  {NIVEAUX.map((n) => (
                    <button
                      key={n.id}
                      type="button"
                      onClick={() => setNiveau(n.id)}
                      className={`px-4 py-3 rounded-lg border text-sm font-medium transition-all ${
                        niveau === n.id
                          ? 'border-tp-primary bg-tp-primary text-white shadow-sm'
                          : 'border-tp-border bg-white text-tp-text hover:border-tp-primary/40'
                      }`}
                      data-testid={`roi-niveau-${n.id}`}
                    >
                      {n.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <span className="text-sm font-medium text-tp-text mb-1.5 block">
                  Durée d’engagement
                </span>
                <div className="grid grid-cols-3 gap-2" role="radiogroup">
                  {DUREES.map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setDuree(d)}
                      className={`px-3 py-3 rounded-lg border text-sm font-medium transition-all ${
                        duree === d
                          ? 'border-tp-primary bg-tp-primary text-white shadow-sm'
                          : 'border-tp-border bg-white text-tp-text hover:border-tp-primary/40'
                      }`}
                      data-testid={`roi-duree-${d}`}
                    >
                      {d}&nbsp;mois
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label
                  htmlFor="roi-nb"
                  className="text-sm font-medium text-tp-text mb-1.5 flex items-center justify-between"
                >
                  <span>Nombre de talents</span>
                  <span className="text-tp-primary font-bold text-base tabular-nums">
                    {nbTalents}
                  </span>
                </label>
                <input
                  id="roi-nb"
                  type="range"
                  min={1}
                  max={5}
                  step={1}
                  value={nbTalents}
                  onChange={(e) => setNbTalents(Number(e.target.value))}
                  className="w-full accent-tp-accent"
                  data-testid="roi-nb-talents"
                />
                <div className="flex justify-between text-[11px] text-tp-text-light mt-1">
                  <span>1</span>
                  <span>2</span>
                  <span>3</span>
                  <span>4</span>
                  <span>5</span>
                </div>
              </div>
            </div>
          </div>

          {/* RESULTS */}
          <div className="bg-tp-primary text-white rounded-2xl shadow-lg p-6 sm:p-8 relative overflow-hidden">
            <div
              aria-hidden
              className="absolute -top-20 -right-20 h-64 w-64 rounded-full bg-tp-accent/30 blur-3xl"
            />
            <div className="relative">
              <div className="text-xs uppercase tracking-wide text-white/60 font-semibold">
                Votre simulation
              </div>
              <h3 className="font-display text-xl font-semibold mt-1">{poste.label}</h3>

              <div className="mt-6 grid grid-cols-2 gap-2 sm:gap-3">
                <div className="min-w-0 bg-white/5 rounded-lg p-3 sm:p-4 border border-white/10">
                  <div className="text-[10px] sm:text-[11px] uppercase tracking-wide text-white/60 leading-tight">
                    Coût Suisse
                  </div>
                  <div
                    className="text-lg sm:text-2xl font-bold mt-1 tabular-nums break-words leading-tight"
                    data-testid="roi-out-ch"
                  >
                    {fmtCHF(calc.chMonthly)}
                  </div>
                  <div className="text-[11px] text-white/60">par mois</div>
                </div>
                <div className="min-w-0 bg-tp-accent/15 rounded-lg p-3 sm:p-4 border border-tp-accent/30">
                  <div className="text-[10px] sm:text-[11px] uppercase tracking-wide text-tp-accent-light leading-tight">
                    Avec Talent Partner
                  </div>
                  <div
                    className="text-lg sm:text-2xl font-bold mt-1 tabular-nums text-tp-accent-light break-words leading-tight"
                    data-testid="roi-out-tp"
                  >
                    {fmtCHF(calc.tpMonthly)}
                  </div>
                  <div className="text-[11px] text-white/60">par mois</div>
                </div>
              </div>

              <div className="mt-6 bg-gradient-to-br from-tp-accent to-tp-accent-light rounded-xl p-5">
                <div className="flex items-center gap-2 text-white/90 text-xs uppercase tracking-wider font-semibold">
                  <TrendingDown className="h-4 w-4" />
                  Économie estimée
                </div>
                <div className="mt-3 grid grid-cols-1 gap-2.5">
                  <Row label="Par mois" value={fmtCHF(calc.monthlySaving)} testid="roi-out-monthly" big />
                  <Row label="Sur 12 mois" value={fmtCHF(calc.annualSaving)} testid="roi-out-annual" />
                  <Row
                    label={`Sur ${duree} mois (engagement choisi)`}
                    value={fmtCHF(calc.totalSaving)}
                    testid="roi-out-total"
                    highlight
                  />
                </div>
              </div>

              <Button
                onClick={() => {
                  trackLanding('click_cta_primary', {
                    meta: { from: 'roi-calc', poste: posteId, niveau, duree, nb: nbTalents },
                    dedupe: false,
                  });
                  onCtaClick?.({ posteId, niveau, duree, nbTalents });
                }}
                className="w-full mt-5 bg-white text-tp-primary hover:bg-white/90 font-semibold py-6 text-base"
                data-testid="roi-cta"
              >
                Recevoir 3 profils adaptés
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
              <p className="text-[11px] text-white/60 text-center mt-3">
                Estimation indicative — le devis personnalisé dépend du brief réel.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Row = ({ label, value, big, highlight, testid }) => (
  <div className="flex items-center justify-between gap-2 sm:gap-3">
    <span className={`text-white/80 shrink min-w-0 ${big ? 'text-sm' : 'text-xs'}`}>{label}</span>
    <span
      className={`tabular-nums font-bold shrink-0 text-right whitespace-nowrap ${
        highlight
          ? 'text-white text-base sm:text-xl'
          : big
            ? 'text-white text-xl sm:text-2xl'
            : 'text-white text-sm sm:text-base'
      }`}
      data-testid={testid}
    >
      {value}
    </span>
  </div>
);

export default RoiCalculator;
